import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions, PermissionGate } from '../context/PermissionContext';
import { Button } from '../components/ui/button';
import { apprenantService, actionService, reportingService } from '../services/api';
import { Users, LogOut, Briefcase, CalendarDays, FolderOpen, MessageCircle, ClipboardList } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ResponsiveTabs from '../components/ResponsiveTabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useContratAutoRefresh } from '../hooks/useContratAutoRefresh';
import api from '../services/api';

// Composants extraits (refactoring)
import { ReferentBeneficiairesView, ReferentContratsView } from './referent';

// Composants existants
import CalendarView from '../components/CalendarView';
import LiveIndicator from '../components/LiveIndicator';
import MesOffresEmploi from '../components/MesOffresEmploi';
import ValidationDiagnostics from '../components/ValidationDiagnostics';
import Messagerie from '../components/Messagerie';
import MessagerieBadge from '../components/MessagerieBadge';

const ReferentDashboard = () => {
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  // États principaux
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState(null);
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('beneficiaires');
  const [contrats, setContrats] = useState([]);
  const [entreprises, setEntreprises] = useState([]);

  // Auto-refresh contrats
  const handleContratUpdate = useCallback(() => {
    toast.info('Un contrat a été modifié', { duration: 3000 });
    loadContrats();
  }, []);
  
  const { isConnected, isPolling, lastPollTime } = useContratAutoRefresh(handleContratUpdate, 30000, true);

  useEffect(() => {
    loadBeneficiaires();
    loadContrats();
  }, [zone]);

  useEffect(() => {
    if (selectedBeneficiaire) {
      loadBeneficiaireData(selectedBeneficiaire.id);
    }
  }, [selectedBeneficiaire]);

  const loadBeneficiaires = async () => {
    try {
      const response = await apprenantService.getAll(zone);
      setBeneficiaires(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des bénéficiaires');
    } finally {
      setLoading(false);
    }
  };

  const loadContrats = async () => {
    try {
      const [contratsRes, entreprisesRes] = await Promise.all([
        api.get('/contrats'),
        api.get('/entreprises')
      ]);
      setContrats(contratsRes.data);
      setEntreprises(entreprisesRes.data);
    } catch (error) {
      console.error('Erreur chargement contrats:', error);
    }
  };

  const loadBeneficiaireData = async (beneficiaireId) => {
    try {
      const [actionsRes, statsRes] = await Promise.all([
        actionService.getAll(beneficiaireId),
        actionService.getStats(beneficiaireId)
      ]);
      setActions(actionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des données');
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedBeneficiaire) return;
    try {
      const now = new Date();
      const response = await reportingService.getSyntheseIndividuelle(
        selectedBeneficiaire.id, now.getMonth() + 1, now.getFullYear()
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur de génération du PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Espace Référent
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">{user?.prenom} {user?.nom}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <div className="hidden sm:block">
              <LiveIndicator isConnected={isConnected} isPolling={isPolling} lastPollTime={lastPollTime} />
            </div>

            {/* Sélecteur de zone */}
            <Select value={zone || 'all'} onValueChange={(val) => setZone(val === 'all' ? null : val)}>
              <SelectTrigger className="w-32 sm:w-48 h-9 rounded-xl">
                <SelectValue placeholder="Toutes les zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les zones</SelectItem>
                <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
              </SelectContent>
            </Select>

            {/* Cloche / badge messagerie : compteur de messages non lus */}
            <MessagerieBadge onClick={() => setActiveTab('messagerie')} />

            <Button onClick={logout} variant="outline" className="rounded-xl h-9 px-2 sm:px-3">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <ResponsiveTabs
            asTabsListReplacement
            value={activeTab}
            onValueChange={setActiveTab}
            className="mb-6"
            listClassName="bg-white rounded-2xl p-1 shadow-sm border border-slate-200"
            tabClassName="rounded-xl px-4 sm:px-6 data-[state=active]:bg-slate-100"
            tabs={[
              { value: 'beneficiaires', label: 'Bénéficiaires', icon: Users },
              ...(hasPermission('view_contrats') ? [{ value: 'contrats', label: 'Contrats', icon: Briefcase }] : []),
              { value: 'diagnostics', label: 'Diagnostics', icon: ClipboardList },
              { value: 'mes-offres', label: 'Mes Offres', icon: FolderOpen },
              { value: 'messagerie', label: 'Messagerie', icon: MessageCircle },
              ...(hasPermission('view_calendar') ? [{ value: 'calendar', label: 'Calendrier', icon: CalendarDays }] : []),
            ]}
          />

          {/* Tab Bénéficiaires */}
          <TabsContent value="beneficiaires">
            <ReferentBeneficiairesView
              beneficiaires={beneficiaires}
              beneficiaire={selectedBeneficiaire}
              selectedBeneficiaire={selectedBeneficiaire}
              setSelectedBeneficiaire={setSelectedBeneficiaire}
              actions={actions}
              stats={stats}
              onGeneratePDF={handleGeneratePDF}
              onRefresh={loadBeneficiaires}
            />
          </TabsContent>

          {/* Tab Diagnostics de compétences */}
          <TabsContent value="diagnostics">
            <ValidationDiagnostics />
          </TabsContent>

          {/* Tab Contrats */}
          <TabsContent value="contrats">
            <ReferentContratsView 
              contrats={contrats}
              entreprises={entreprises}
              beneficiaires={beneficiaires}
            />
          </TabsContent>

          {/* Tab Mes Offres */}
          <TabsContent value="mes-offres">
            <MesOffresEmploi />
          </TabsContent>

          {/* Tab Messagerie */}
          <TabsContent value="messagerie">
            <Messagerie />
          </TabsContent>

          {/* Tab Calendrier */}
          {hasPermission('view_calendar') && (
            <TabsContent value="calendar">
              <CalendarView userRole="referent" />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default ReferentDashboard;
